import type { PageGroupUserType, PageOutcomeType, SubmissionType } from "components/server-types";
import { t } from "i18n/i18n";
import { isIndividualSubmission } from "store/selectors/type-helpers";
import type { NestedAssessmentType } from "store/selectors/types";
import { averageAndRound } from "utils/math";

export interface PageAssessmentData {
  pointList: number[];
  submissions: SubmissionType[];
  latestSubmissionDate: string | false;
  pageSpecificOutcomes: PageOutcomeType[];
}

export interface StudentAssessmentFilter {
  section_user_id: string;
  page_group_id?: string;
}

export const getPageSpecificAssessmentData = (
  page: { assessments: NestedAssessmentType[]; id: string },
  pageOutcomes: PageOutcomeType[],
  tableDateFormat: (date: string) => string,
): PageAssessmentData => {
  const pointList = page.assessments.map((a) => a.points);
  const submissions = page.assessments.map((a) => a.submission);
  const submissionsByDate = submissions
    .slice(0)
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  const latestSubmissionDate =
    submissionsByDate.length > 0 && tableDateFormat(submissionsByDate[0].created_at);
  const pageSpecificOutcomes = pageOutcomes.filter((po) => po.page_id === page.id);

  return {
    pointList,
    submissions,
    latestSubmissionDate,
    pageSpecificOutcomes,
  };
};

export const filterAssessmentsForStudent = (
  pageAssessments: NestedAssessmentType[],
  student: StudentAssessmentFilter,
  pageGroupUsers: PageGroupUserType[],
): NestedAssessmentType[] => {
  return pageAssessments.filter((a) => {
    const submission = a.submission;
    return isIndividualSubmission(submission)
      ? submission.section_user_id === student.section_user_id
      : pageGroupUsers.find(
          (pgu) =>
            pgu.page_group_id === submission.page_group_id &&
            pgu.section_user_id === student.section_user_id,
        );
  });
};

export const calculateStudentOutcomeScore = (
  userAssessments: NestedAssessmentType[],
  courseOutcomeId: string,
  pageOutcomes: PageOutcomeType[],
): number | null => {
  const userOutcomeAssessmentsForThisOutcome = userAssessments
    .map((a) => a.outcome_assessments || [])
    .flat()
    .filter(
      (oa) =>
        pageOutcomes.find((po) => po.id === oa.page_outcome_id)?.section_outcome_id ===
        courseOutcomeId,
    );

  return userOutcomeAssessmentsForThisOutcome.length
    ? averageAndRound(userOutcomeAssessmentsForThisOutcome.map((oa) => oa.score))
    : null;
};

/**
 * Returns the localized assessment status label based on completion state.
 * Used across assessment views to display consistent status text.
 *
 * Note: Callers should check for NOT_ASSESSABLE status before calling this function
 * if needed. HomeAssessmentList doesn't need this check (server pre-filters),
 * while CourseAssessmentView and hooks.ts do.
 */
export const getAssessmentStatusLabel = (
  isMarkedComplete: boolean,
  assessedSubmissionsCount: number,
  isAssessmentComplete: boolean,
): string => {
  if (isMarkedComplete) {
    return assessedSubmissionsCount === 0 ? t("common.completed") : t("assessment_status.assessed");
  }

  return isAssessmentComplete ? t("assessment_status.assessed") : t("assessment_status.assessable");
};
